import type { CalendarDate } from '@internationalized/date'
import { endOfMonth, getWeeksInMonth, startOfWeek, today } from '@internationalized/date'
import { useCalendarGrid } from '@react-aria/calendar'
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar'
import type { ReactNode } from 'react'
import { forwardRef, useMemo } from 'react'
import { useLocale } from 'react-aria'
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone'

export type GridProps = {
  state: CalendarState | RangeCalendarState
  startDate: CalendarDate
  tbodyProps?: React.HTMLAttributes<HTMLTableSectionElement>
  renderWeekDay: (date: Date) => ReactNode
  children: (week: number) => ReactNode
}

export const Grid = forwardRef<HTMLTableElement, GridProps>(
  ({ state, startDate, tbodyProps, renderWeekDay, children }, ref) => {
    const { locale } = useLocale()

    const endDate = endOfMonth(startDate)
    const { gridProps, headerProps } = useCalendarGrid({ startDate, endDate }, state)

    const weekDays = useMemo(() => {
      const weekStart = startOfWeek(today(DEFAULT_TIME_ZONE), locale)

      return Array.from({ length: 7 }, (_, index) =>
        weekStart.add({ days: index }).toDate(DEFAULT_TIME_ZONE)
      )
    }, [locale])

    const weeksInMonth = getWeeksInMonth(startDate, locale)

    return (
      <table
        {...gridProps}
        data-calendar-month={`${startDate.year}-${startDate.month}`}
        cellPadding="0"
        className="block sm:flex-1 sm:border-separate sm:border-spacing-y-6"
        ref={ref}
      >
        <thead {...headerProps} className="block border-primary/15 border-b px-4 pb-2 text-center">
          <tr className="grid grid-cols-7">
            {weekDays.map((day, index) => (
              <th key={index} className="font-base-copy font-semibold">
                {renderWeekDay(day)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="block min-h-70 px-4 py-2" {...tbodyProps}>
          {[...Array.from({ length: weeksInMonth }).keys()].map(children)}
        </tbody>
      </table>
    )
  }
)

Grid.displayName = 'Grid'
